"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, useMutation } from "@opensea/graphql";
import { EmptyState, TableScrollContainerContext, VirtualizedTable } from "@opensea/ui-kit";
import { useItemsWithSkeletons, useMountEffect } from "@opensea/ui-kit/hooks";
import { range } from "es-toolkit/math";
import { useTranslations } from "next-intl";
import { useContext } from "react";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { usePaginatedQuery } from "@/hooks/api/use-paginated-query";
import { useAddress } from "@/hooks/wallet";
import { PAGE_SIZE } from "./constants";
import { NotificationsTableRow } from "./NotificationsTableRow";
import { NotificationsTableRowFragment } from "./NotificationsTableRow.fragment";
import { useNotificationSubscription } from "./use-notification.subscription";
const query = graphql(`
    query NotificationsSheetQuery(
      $address: Address!
      $limit: Int
      $cursor: String
    ) {
      notifications(address: $address, limit: $limit, cursor: $cursor) {
        items {
          __typename
          ... on ItemBoughtNotification {
            occurredAt
            isRead
          }
          ... on ItemSoldNotification {
            occurredAt
            isRead
          }
          ... on BulkPurchaseNotification {
            occurredAt
            isRead
          }
          ... on BulkSaleNotification {
            occurredAt
            isRead
          }
          ... on RewardReadyToClaimNotification {
            occurredAt
            isRead
          }
          ... on MintNotification {
            occurredAt
            isRead
          }
          ... on DropStageReminderNotification {
            occurredAt
            isRead
          }
          ... on ShipmentReceivedNotification {
            occurredAt
            isRead
          }
          ...NotificationsTableRow
        }
        nextPageCursor
      }
    }
  `, [NotificationsTableRowFragment]);
const readMutation = graphql(`
  mutation updateNotificationTimestamp($address: Address!) {
    updateNotificationTimestamp(address: $address)
  }
`);
export const NotificationsTable = withSuspense(function NotificationsTable({
  setUnreadCount
}) {
  const t = useTranslations("NotificationsTable");
  const address = useAddress();
  const scrollContainerRef = useContext(TableScrollContainerContext);
  const [_, updateNotificationTimestamp] = useMutation(readMutation);
  const {
    items,
    noResults,
    pagination,
    setItems
  } = usePaginatedQuery({
    query,
    variables: {
      address: address ?? "",
      limit: PAGE_SIZE
    },
    pause: !address
  }, v => v?.notifications, {
    pageSize: PAGE_SIZE
  });
  const itemsWithSkeletons = useItemsWithSkeletons({
    items,
    pageSize: PAGE_SIZE,
    isLoadingNext: pagination.isLoadingNext
  });
  useNotificationSubscription({
    address,
    setItems
  });
  useMountEffect(() => {
    const readNotifications = async () => {
      if (address) {
        await updateNotificationTimestamp({
          address
        });
        setUnreadCount(0);
      }
    };
    void readNotifications();
  });
  if (noResults) {
    return <EmptyState className="h-full px-6" description={t("emptyStateDescription")} title={t("emptyStateTitle")} variant="no-results" />;
  }
  return <VirtualizedTable itemKey={item => `${String(item?.__typename)}-${String(item?.occurredAt)}`} items={itemsWithSkeletons} renderRow={NotificationsTableRow} scrollContainerRef={scrollContainerRef} scrollMode="table" size="lg" {...pagination} />;
}, {
  fallback: NotificationsTableSkeleton
});
function NotificationsTableSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <VirtualizedTable itemKey={_temp} items={range(PAGE_SIZE).map(_temp2)} renderRow={NotificationsTableRow} size="lg" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2() {}
function _temp(_, index) {
  return index;
}